<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading || initialLoading" :scrollable="false">
      <spinner :show="initialLoading" v-if="initialLoading"/>
      <div v-else>
        <div class="row">
          <div class="col-md-4" v-for="filter in displayFilters"
               :key="`report-generation-field-${filter.key}`">
            <template v-if="filter.type === 'string'">
              <user-select-input-widget v-if="filter.key === 'account_manager_uuids'" :model="model"
                                        :attribute="filter.key" :options="{multiple: true}"/>
              <user-select-input-widget v-else-if="filter.key === 'rep_user_uuids'" :model="model"
                                        :attribute="filter.key" :options="{multiple: true}"/>
              <receipt-select-input-widget v-else-if="filter.key === 'receipt_uuids'" :model="model"
                                           :attribute="filter.key" :options="{multiple: true}"/>
              <sale-select-input-widget v-else-if="filter.key === 'auction_uuids'" :model="model"
                                        :attribute="filter.key" :options="{multiple: true}"
                                        :disabled="disableSalesSelect" :remove-title-from-search="enableContinuousAuction"
                                        :remove-title-from-selection="enableContinuousAuction"/>
              <item-location-select-input-widget v-else-if="filter.key === 'item_location_uuids'" :model="model"
                                        :attribute="filter.key" :multiple="true" :flat="true"
                                        :remove-title-from-search="enableContinuousAuction"
                                        :remove-title-from-selection="enableContinuousAuction"/>
              <language-code-select-input-widget v-else-if="filter.key === 'preferred_language_code'"
                                                 :model="model" attribute="preferred_language_code"/>
              <customer-select-input-widget v-else-if="filter.key === 'customer_uuids'" :model="model"
                                            :attribute="filter.key" :options="{multiple: true}"/>
              <invoice-select-input-widget v-else-if="filter.key === 'invoice_uuids'" :model="model"
                                           :attribute="filter.key" :options="{multiple: true}"/>
              <settlement-select-input-widget v-else-if="filter.key === 'settlement_uuids'" :model="model"
                                              :attribute="filter.key" :options="{multiple: true}"/>
              <input-widget v-else-if="filter.key === 'payment_methods'" :model="model" :attribute="filter.key"
                            type="select2"
                            :items="filter.options" value-field="key" display-field="${item.text}"
                            :options="{multiple: true}"/>
              <user-select-input-widget
                v-else-if="filter.key === 'specialists_uuids'"
                :model="model" is-search
                                            :attribute="filter.key" :options="{multiple: true}"/>
              <cataloguing-lifecycle-select-input-widget v-else-if="filter.key === 'cataloguing_lifecycle_values'"
                :model="model" :attribute="filter.key"
                            :multiple="true" />
              <photography-lifecycle-select-input-widget v-else-if="filter.key === 'photography_lifecycle_values'"
                :model="model" :attribute="filter.key" :multiple="true" />
              <input-widget v-else-if="filter.key === 'lot_status_values'" :model="model" :attribute="filter.key"
                type="select2" :items="lotStatuses" value-field="value" display-field="${item.key}"
                :options="{multiple: true}"/>
              <input-widget v-else-if="filter.key === 'reservation_status_values'" :model="model" :attribute="filter.key"
                type="select2" :items="reservationStatuses" value-field="value" display-field="${item.key}"
                :options="{multiple: true}"/>
              <input-widget v-else-if="filter.key === 'tag'" :model="model" :attribute="filter.key" type="select2"
                            :items="filter.options" value-field="key" display-field="${item.text}"/>
              <department-select-input-widget v-else-if="filter.key === 'department_uuids'" :model="model"
                :attribute="filter.key" :multiple="true" :options="{multiple: true}"/>
              <multiple-category-select-input-widget v-else-if="filter.key === 'category_uuids'" :model="model"
                :label="$t('Categories')" :attribute="filter.key" :options="{multiple: true}"/>
              <multiple-category-select-input-widget v-else-if="filter.key === 'filtered_category_uuids'" :model="model"
                :label="$t('Categories')" :attribute="filter.key" :options="{multiple: true}"
                :limit-by-selected-departments="true"
                :selected-department-uuids="model.department_uuids || []"
              />
              <div v-else-if="filter.key === 'hammer_price_min' && hasHammerPriceRangeFilters" class="w-100 hammer-price-range-block">
                <b-form-group :label="$t('Hammer Price')">
                  <div class="d-flex flex-nowrap align-items-center hammer-price-range-inline">
                    <span class="hammer-price-range-inline__text">{{ $t('Min') }}:</span>
                    <div class="hammer-price-range-inline__field">
                      <input-widget :model="model" attribute="hammer_price_min" type="number" :label="false" :min="0"
                                    vid="report_gen_hammer_price_min" :rules="hammerPriceMinRules"/>
                    </div>
                    <span class="hammer-price-range-inline__text">{{ $t('Max') }}:</span>
                    <div class="hammer-price-range-inline__field">
                      <input-widget :model="model" attribute="hammer_price_max" type="number" :label="false" :min="0"
                                    vid="report_gen_hammer_price_max" :rules="hammerPriceMaxRules"/>
                    </div>
                  </div>
                </b-form-group>
              </div>
              <input-widget v-else-if="(filter.key === 'hammer_price_min' || filter.key === 'hammer_price_max') && !hasHammerPriceRangeFilters"
                            :model="model" :attribute="filter.key" type="number" :min="0"
                            :vid="filter.key === 'hammer_price_min' ? 'report_gen_hammer_price_min' : 'report_gen_hammer_price_max'"
                            rules="optional_non_negative"/>
              <input-widget v-else :model="model" :attribute="filter.key"/>
            </template>
            <template v-if="filter.type === 'list'">
              <input-widget :model="model" :attribute="filter.key" type="select2" :items="filter.options"
                            value-field="key" display-field="${item.text}"/>
            </template>
            <template v-if="filter.type === 'relational'">
              <template v-if="filter.key === 'customer_uuid'">
                <customer-select-input-widget :model="model" :attribute="filter.key"/>
              </template>
              <template v-if="filter.key === 'receipt_uuid'">
                <receipt-select-input-widget :model="model" :attribute="filter.key"/>
              </template>
              <template v-if="filter.key === 'department_uuid'">
                <department-select-input-widget :model="model" :attribute="filter.key"/>
              </template>
              <template v-if="filter.key === 'auction_uuid'">
                <sale-select-input-widget :model="model" :attribute="filter.key"/>
              </template>
              <template v-if="filter.key === 'vendor_uuid'">
                <input-widget :model="model" :attribute="filter.key" type="select2"
                              display-field="${item.key}" value-field="value" :items="vendors"/>
              </template>
            </template>
            <template v-if="filter.type === 'date_range'">
              <input-widget v-if="filter.key === 'sale_start_range'" :model="model" :attribute="filter.key"
                            type="daterangepicker" :disabled="disableSaleStartRange"/>
              <input-widget v-else-if="filter.key === 'receive_date'" :model="model" :attribute="filter.key"
                            type="daterangepicker"/>
              <input-widget v-else-if="filter.key === 'released_date'" :model="model" :attribute="filter.key"
                            type="daterangepicker"/>
              <input-widget v-else :model="model" :attribute="filter.key" type="daterangepicker"/>
            </template>
            <template v-if="filter.type === 'date'">
              <input-widget :model="model" :attribute="filter.key" type="datepicker"/>
            </template>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4" v-for="filter in filters.filter(item => item.type === 'boolean')"
               :key="`report-generation-field-${filter.key}`">
            <template>
              <input-widget :model="model" :attribute="filter.key" type="checkbox"/>
            </template>
          </div>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import ReportsService from "@/modules/reports/ReportsService";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import InvoiceSelectInputWidget from "@/core/widgets/InvoiceSelectInputWidget";
  import SettlementSelectInputWidget from "@/core/widgets/SettlementSelectInputWidget";
  import Spinner from "@/core/components/common/Spinner";
  import ReportGenerationModel from "@/modules/reports/ReportGenerationModel.model";
  import SaleSelectInputWidget from "../../core/widgets/SaleSelectInputWidget";
  import SalesService from "@/modules/sales/SalesService";
  import ReceiptSelectInputWidget from "@/core/widgets/ReceiptSelectInputWidget";
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import i18n from "@/shared/i18n";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import DepartmentSelectInputWidget from "@/core/widgets/DepartmentSelectInputWidget.vue";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget.vue";
  import CataloguingLifecycleSelectInputWidget from "@/core/widgets/CataloguingLifecycleSelectInputWidget.vue";
  import PhotographyLifecycleSelectInputWidget from "@/core/widgets/PhotographyLifecycleSelectInputWidget.vue";
  import MultipleCategorySelectInputWidget from "@/core/widgets/MultipleCategorySelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('reports');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ReportGenerationModal",
    components: {
      PhotographyLifecycleSelectInputWidget,
      CataloguingLifecycleSelectInputWidget,
      ItemLocationSelectInputWidget,
      DepartmentSelectInputWidget,
      MultipleCategorySelectInputWidget,
      LanguageCodeSelectInputWidget,
      UserSelectInputWidget,
      ReceiptSelectInputWidget,
      Spinner,
      SaleSelectInputWidget,
      CustomerSelectInputWidget,
      InvoiceSelectInputWidget,
      SettlementSelectInputWidget,
      InputWidget, ArtisioModal
    },
    data() {
      return {
        loading: false,
        initialLoading: false,
        showConfirmation: true,
        model: new ReportGenerationModel(),
        filters: [],
        vendors: [],
        required_fields: {},
        optional_fields: {}
      }
    },
    computed: {
      ...mapState({
        modal: state => state.reportGenerationModal,
        report: state => state.reportGenerationModal.report
      }),
      ...globalMapState({
        currentUser: state => state.currentUser,
        enableContinuousAuction: state => state.enableContinuousAuction,
        lotStatuses: state => state.lotStatuses,
        reservationStatuses: state => state.reservationStatuses,
      }),
      disableSalesSelect() {
        return !!(this.model.sale_start_range)
      },
      disableSaleStartRange() {
        return !!(this.model.auction_uuids && this.model.auction_uuids.length > 0)
      },
      /** Non-boolean filters; merges hammer min+max into one row when both exist. */
      displayFilters() {
        const list = (this.filters || []).filter(item => item.type !== 'boolean');
        const keys = new Set(list.map(f => f.key));
        if (keys.has('hammer_price_min') && keys.has('hammer_price_max')) {
          return list.filter(x => x.key !== 'hammer_price_max');
        }
        return list;
      },
      hasHammerPriceRangeFilters() {
        const keys = new Set(
          (this.filters || []).filter(item => item.type !== 'boolean').map(f => f.key)
        );
        return keys.has('hammer_price_min') && keys.has('hammer_price_max');
      },
      hammerPriceMinRules() {
        if (!this.hasHammerPriceRangeFilters) {
          return 'optional_non_negative';
        }
        return 'optional_non_negative|report_hammer_min_lte_max:@report_gen_hammer_price_max';
      },
      hammerPriceMaxRules() {
        if (!this.hasHammerPriceRangeFilters) {
          return 'optional_non_negative';
        }
        return 'optional_non_negative|report_hammer_max_gte_min:@report_gen_hammer_price_min';
      },
      computedTitle() {
        if (this.report && this.report.display_name) {
          return this.$i18n.t("Generate Report: {report_name}", {report_name: this.report.display_name})
        }
        return this.$i18n.t("Generate Report")
      }
    },
    watch: {
      async report() {
        if (this.report) {
          this.initialLoading = true;
          await this.initializeReportGenerationModel(
              this.report.available_filters.filter(i => !i.required),
          )
          this.initialLoading = false;
        }
      },
      'model.auction_uuid': {
        deep: true,
        async handler() {
          if (this.model.auction_uuid) {
            let response = await SalesService.getSaleVendors(this.model.auction_uuid)
            if (response.success) {
              this.vendors = []
              for (let data of response.body) {
                this.vendors.push({'key': data.display_name, 'value': data.uuid})
              }
            }
          }
        }
      },
      'model': {
        deep: true,
        handler() {
          for (let [field, dependencies] of Object.entries(this.required_fields)) {
            let field_required = false
            for (let dependency of dependencies) {
              if (this.model[dependency]) {
                field_required = true
                break
              }
            }
            this.model.rules[field] = field_required ? 'required' : ''
          }
          for (let [field, dependencies] of Object.entries(this.optional_fields)) {
            let field_required = true
            for (let dependency of dependencies) {
              const value = this.model[dependency];
              if (value !== undefined &&
                value !== null &&
                (
                  Array.isArray(value)
                    ? value.length > 0
                    : value !== ''
                )
              ) {
                field_required = false
                break
              }
            }
            this.model.rules[field] = field_required ? 'required' : ''
          }
        }
      }
    },
    methods: {
      ...mapActions(['hideReportGenerationModal']),
      onShow() {
        this.showConfirmation = true
      },
      onHidden() {
        this.filters = [];
        this.required_fields = {}
        this.optional_fields = {}
        this.model = new ReportGenerationModel();
        this.hideReportGenerationModal();
        this.vendors = []
      },
      async prepareListFilterOptions(filters) {
        for (let i in filters) {
          if (filters[i].key.endsWith('_boolean')) {
            filters[i].type = 'boolean'
          }
          if (filters[i].key == 'tag' && !filters[i].options) {
            let tagsResponse = await ReportsService.getExistingTags();
            if (tagsResponse.success) {
              filters[i].options = tagsResponse.body.map(val => {
                return {
                  key: val, text: this.$options.filters.toUpperCaseFirst(val)
                }
              });
            }
          }
          if (filters[i].type === 'list' || ['payment_methods'].includes(filters[i].key)) {
            filters[i].options = filters[i].options.map(val => {
              return {
                key: val, text: this.$options.filters.toUpperCaseFirst(val)
              }
            })
          }
          if (filters[i].required_fields.length) {
            this.required_fields[filters[i].key] = filters[i].required_fields
          }
          if (filters[i].optional_fields.length) {
            this.optional_fields[filters[i].key] = filters[i].optional_fields
          }
        }
      },
      async initializeReportGenerationModel(filters) {
        // Convert filter options to objects array instead of strings array
        this.prepareListFilterOptions(filters);

        let dataForObj = new ReportGenerationModel();

        // Initialize model related attributes
        for (let item of filters) {
          dataForObj[item.key] = null;
          if (['cataloguing_lifecycle_values', 'photography_lifecycle_values', 'department_uuids', 'category_uuids',
            'filtered_category_uuids', 'reservation_status_values', 'lot_status_values'].indexOf(item.key) > -1) {
            dataForObj[item.key] = [];
          }
          dataForObj.attributeLabels[item.key] = item.label;
          dataForObj.rules[item.key] = item.active_required ? "required" : "";
          if (item.type === 'date_range') {
            dataForObj[`${item.key}_date_range`] = {};
          }
        }

        this.model = new ReportGenerationModel(dataForObj);
        this.filters = filters;
        return true;
      },
      getRequestParams() {
        let params = {}
        for (let item of this.filters) {
          if (['account_manager_uuids', 'rep_user_uuids', 'receipt_uuids', 'auction_uuids', 'customer_uuids', 'invoice_uuids',
            'settlement_uuids', 'cataloguing_lifecycle_values', 'photography_lifecycle_values',
            'specialists_uuids', 'item_location_uuids', 'department_uuids', 'category_uuids', 'filtered_category_uuids',
            'reservation_status_values', 'lot_status_values'].includes(item.key)) {
            params[item.key] = this.model[item.key] ? this.model[item.key].join(',') : '';
          } else {
            params[item.key] = this.model[item.key];
          }
        }
        return params;
      },
      async onSubmit() {
        this.showConfirmation = false;
        let validationResponse = await ReportsService.validateReport(this.report.uuid);
        if (validationResponse.success) {
          let params = this.getRequestParams();
          if (this.report.type === 'file_download') {
            await ReportsService.generateAndDownloadReport(this.report.uuid, params);
          } else if (this.report.type === 'serverless') {
            await ReportsService.generateReport(this.report.uuid, params);
            this.$toast('info', i18n.t("Please wait until report is ready."));
          } else if (this.report.type === 'celery') {
            await ReportsService.generateReport(this.report.uuid, params);
            this.$toast('info', i18n.t("Please wait until report is ready."));
          }
          this.hideReportGenerationModal()
        } else {
          this.showConfirmation = true;
          this.$errorDialog(validationResponse.body.non_field_errors[0])
        }
      },
    }
  }
</script>

<style scoped>
  .hammer-price-range-inline {
    gap: 0.35rem 0.5rem;
  }

  .hammer-price-range-inline__text {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .hammer-price-range-inline__field {
    flex: 1 1 0;
    min-width: 6rem;
    max-width: none;
    width: auto;
  }

  .hammer-price-range-inline__field ::v-deep .form-group {
    margin-bottom: 0;
  }

  .hammer-price-range-inline__field ::v-deep .b-form-input {
    width: 100%;
    min-width: 0;
  }
</style>
